import React, { useMemo, useState } from 'react';
import { useAdminMotivos } from '../../hooks/useAdminMotivos.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const iconSuggestions = ['wrench', 'sliders-horizontal', 'alert-triangle', 'arrow-up', 'arrow-down', 'sparkles'];

const normalizeIcon = (icon) => {
  if (!icon) {
    return 'wrench';
  }
  if (icon === 'adjustments') {
    return 'sliders-horizontal';
  }
  return icon;
};

const MotivosSection = ({ onNotify }) => {
  const { items, loading, error, refresh, saveMotivo, deleteMotivo } = useAdminMotivos();
  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items, editorState.open, search]);

  const filteredItems = useMemo(() => {
    if (!search) {
      return items;
    }
    const query = search.toLowerCase();
    return items.filter((item) => {
      const name = item.nombre?.toLowerCase() || '';
      const description = item.descripcion?.toLowerCase() || '';
      return name.includes(query) || description.includes(query);
    });
  }, [items, search]);

  const handleOpenEditor = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? { ...record, icono: normalizeIcon(record.icono) }
        : {
          nombre: '',
          descripcion: '',
          icono: 'wrench',
          activo: true
        }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      const payload = {
        ...values,
        icono: normalizeIcon(values.icono)
      };
      await saveMotivo(payload);
      setEditorState({ open: false, saving: false, record: null });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Motivo actualizado correctamente.' : 'Motivo creado correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar el motivo.' });
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`¿Eliminar el motivo "${record.nombre}"?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteMotivo(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Motivo eliminado.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar el motivo.' });
    }
  };

  const closeEditor = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="motivos-ajuste-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Motivos de Ajuste</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre o descripción..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <button className="btn-primary" type="button" onClick={() => handleOpenEditor()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nuevo Motivo
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando motivos...</div>
          </div>
        ) : (
          <table className="data-table" id="motivos-ajuste-table">
            <thead>
              <tr>
                <th>Icono</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="inbox" /></div>
                    <div className="empty-state-text">No hay motivos configurados.</div>
                    <div className="empty-state-subtext">Crea tu primer motivo para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((motivo) => (
                  <tr key={motivo.id}>
                    <td>
                      <i data-lucide={normalizeIcon(motivo.icono)} />
                    </td>
                    <td><strong>{motivo.nombre}</strong></td>
                    <td>{motivo.descripcion || '-'}</td>
                    <td>
                      <span className={`status-badge ${motivo.activo ? 'status-active' : 'status-inactive'}`}>
                        {motivo.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="action-btn edit"
                          onClick={() => handleOpenEditor(motivo)}
                          title="Editar"
                        >
                          <i data-lucide="edit" />
                        </button>
                        <button
                          type="button"
                          className="action-btn delete"
                          onClick={() => handleDelete(motivo)}
                          title="Eliminar"
                        >
                          <i data-lucide="trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <MotivoEditorModal
          state={editorState}
          onClose={closeEditor}
          onSubmit={handleSave}
        />
      ) : null}
    </section>
  );
};

const MotivoEditorModal = ({ state, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(state.record || {});

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formValues });
  };

  const currentIcon = formValues.icono || 'wrench';

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h3>{formValues?.id ? 'Editar Motivo' : 'Nuevo Motivo'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nombre</label>
            <input
              type="text"
              className="form-input"
              name="nombre"
              value={formValues.nombre || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Descripción</label>
            <textarea
              className="form-input"
              name="descripcion"
              value={formValues.descripcion || ''}
              onChange={handleChange}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Icono (Lucide)</label>
            <div className="icon-picker">
              <input
                type="text"
                className="form-input"
                name="icono"
                value={currentIcon}
                onChange={handleChange}
              />
              <div className="icon-preview">
                <i data-lucide={currentIcon || 'wrench'} />
              </div>
            </div>
            <div className="icon-suggestions">
              {iconSuggestions.map((icon) => (
                <button
                  type="button"
                  key={icon}
                  className={`icon-chip ${icon === currentIcon ? 'active' : ''}`}
                  onClick={() => setFormValues((prev) => ({ ...prev, icono: icon }))}
                >
                  <i data-lucide={icon} />
                  <span>{icon}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="form-group checkbox-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo ?? true}
                onChange={handleChange}
              />
              <span className="checkmark" />
              Motivo activo
            </label>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MotivosSection;
